import { DRM_ACCOUNT_TYPES, URL_PATTERN_FOR_TEMPLATES } from '../../../../app/core/constants'
import {
  transcodingStatusDisplayText,
  transcodingStatusStateCssClass
} from '../../../../app/helpers/streamWorkState'
import {
  highlightClassSimple,
  TypeaheadClass
} from '../../../../app/helpers/typeahead'
import { changeStreamUrl, filterDRM, getFilteredKeys, multiSelectWithAll } from './ciHelpers'
import { streamPolicyOperators, streamPolicyPropertiesNames } from '../../../streams/urlRule.directive'

/**
 * Directive for Stream Policy.
 *
 * @class ciStreamPolicy
 * @memberof mwApp/common
 * @see mwApp/common
 *
 * @ngdoc directive
 * @name mwApp/common:ciStreamPolicy
 *
 */
/* @ngInject */
export function ciStreamPolicy (UrlPolicy, TemplateVariable, $uibModal, $http, alertLiteSvc, Stream, SetrixServer, InappSettings, authSvc) {
  function getCondition (scope) {
    return (conditionRule) => UrlPolicy.getCondition(conditionRule, scope.entity)
  }

  function canModifyAssignmentTranscoder () {
    return !!authSvc?.hasAccess?.('modifyAssignmentTranscoder')
  }

  function toStreamBinding (modalResult) {
    if (!modalResult) return null

    const streamId = modalResult.transcoderId ?? null
    if (!streamId || !modalResult.setrixServerId) return null

    return {
      streamId,
      streamName: modalResult.transcoderName ?? modalResult.templateName ?? null,
      server: {
        id: modalResult.setrixServerId,
        name: modalResult.setrixServerName ?? null,
        networkId: modalResult.setrixServerNetworkId ?? null
      }
    }
  }

  return {
    restrict: 'E',
    templateUrl: 'components/common/directives/contentItems/views/ciStreamPolicy.html',
    scope: {
      form: '=form',
      entity: '=entity',
      drmProfiles: '=drmProfiles',
      playbackTokenGenerators: '=playbackTokenGenerators'

    },
    link: function (scope) {
      scope.transcodingStatusStateCssClass = transcodingStatusStateCssClass
      scope.transcodingStatusDisplayText = transcodingStatusDisplayText
      scope.hasAccess = function (access) {
        return !!authSvc?.hasAccess?.(access, scope)
      }
      scope.urlPolicies = []
      scope.loadPolicies = UrlPolicy.getPolicies(scope.urlPolicies)
      scope.getCondition = getCondition(scope)
      scope.loadPolicies()

      let setrixPrefetchKey = null
      function unwrapSetrixPayload (data) {
        if (!data) return data
        if (typeof data === 'object' && data.status && data.payload) return data.payload
        if (typeof data === 'object' && data.payload) return data.payload
        return data
      }
      function pickSetrixServerList (payload) {
        if (!payload) return []
        if (Array.isArray(payload)) return payload
        return (
          payload.streams ||
          payload.items ||
          payload.data ||
          payload.results ||
          payload.content ||
          []
        )
      }
      function getSetrixServerListFromResponse (response) {
        return pickSetrixServerList(unwrapSetrixPayload(response?.data))
      }
      function assignedNetworkIdSignature () {
        const list = Array.isArray(scope.entity?.networks) ? scope.entity.networks : []
        const ids = list
          .map((n) => n?.id)
          .filter((id) => id != null && String(id).trim() !== '')
          .map((id) => String(id))
          .sort()
        return ids.join(',')
      }
      function buildAllowedNetworkIdSet () {
        const sig = assignedNetworkIdSignature()
        if (!sig) return null
        return new Set(sig.split(',').filter(Boolean))
      }
      function filterServersByAssignedNetworks (servers, multiorgEnabled) {
        const list = Array.isArray(servers) ? servers : []
        if (!multiorgEnabled) return list
        const allowed = buildAllowedNetworkIdSet()
        if (!allowed || allowed.size === 0) return list
        return list.filter((s) => {
          const id = s?.networkId
          if (id == null) return false
          return allowed.has(String(id))
        })
      }
      function refreshSetrixServersPrefetch () {
        const multiorgEnabled = !!InappSettings?._current?.multiorgEnabled
        const key = `${multiorgEnabled ? 1 : 0}:${
          multiorgEnabled ? assignedNetworkIdSignature() : (scope.entity?.network?.id ?? '')
        }`
        if (key === setrixPrefetchKey && scope.setrixServersPrefetchPromise) return
        setrixPrefetchKey = key
        scope.hasAvailableSetrixServers = false
        // In multi-org mode we intentionally fetch all servers and filter client-side
        // by content-assigned organizations (entity.networks[].id vs server.networkId).
        const requestNetworkId = multiorgEnabled ? null : (scope.entity?.network?.id ?? null)
        scope.setrixServersPrefetchPromise = SetrixServer.loadAll(requestNetworkId, multiorgEnabled)
        scope.setrixServersPrefetchPromise
          .then((response) => {
            const list = getSetrixServerListFromResponse(response)
            const filtered = filterServersByAssignedNetworks(list, multiorgEnabled)
            scope.setrixServersPrefetchList = filtered
            scope.hasAvailableSetrixServers = Array.isArray(filtered) && filtered.length > 0

            const byId = new Map(
              (Array.isArray(list) ? list : [])
                .filter((s) => s && (s.id != null || s.serverId != null))
                .map((s) => [String(s.id ?? s.serverId), s.name ?? s.serverName ?? null])
            )

            const bindings = Array.isArray(scope.entity?.streamBindings) ? scope.entity.streamBindings : []
            bindings.forEach((binding) => {
              const serverId = binding?.server?.id
              if (serverId == null) return
              const serverName = byId.get(String(serverId))
              if (!serverName) return
              if (!binding.server) binding.server = { id: serverId }
              binding.server.name = binding.server.name ?? serverName
            })
          })
          .catch(() => {
            scope.setrixServersPrefetchList = []
            scope.hasAvailableSetrixServers = false
          })
      }
      refreshSetrixServersPrefetch()
      scope.$watchGroup([
        () => !!InappSettings?._current?.multiorgEnabled,
        assignedNetworkIdSignature,
        () => scope.entity?.network?.id
      ], refreshSetrixServersPrefetch)

      scope.isStreamPolicyOpen = scope.entity._isNew()
      scope.urlPattern = URL_PATTERN_FOR_TEMPLATES

      TemplateVariable.loadAllVariables()
        .to(scope.templateVariables = [])
        .then(() => {
          scope.setDefaultStreamUrlValue = function (val) {
            scope.entity.defaultStream.mediaUrl.mediaUrl = val
          }
          scope.typeaheadUrl = new TypeaheadClass(scope.templateVariables, scope.setDefaultStreamUrlValue, changeStreamUrl)

          scope.setDefaultTrailerUrlValue = function (val) {
            scope.entity.defaultStream.mediaUrl.trailerUrl = val
          }
          scope.typeaheadDefaultTrailerUrl = new TypeaheadClass(scope.templateVariables, scope.setDefaultTrailerUrlValue, changeStreamUrl)
        })

      scope.highlightClassSimple = (model) => highlightClassSimple(scope.templateVariables, model)

      scope.countUrls = function (arr = scope.entity.streamPolicy.$rules) {
        return arr?.reduce((acc, rule) => acc + !!rule?.then?.mediaUrl?.mediaUrl + scope.countUrls(rule.$subRules), 0)
      }

      scope.getFilteredKeys = getFilteredKeys
      scope.multiSelectWithAll = multiSelectWithAll
      let streamStatusesPollingId = null
      let streamStatusesInitialized = false

      scope.requestAssignedStreamsStatus = function () {
        const bindings = Array.isArray(scope.entity.streamBindings) ? scope.entity.streamBindings : []
        const streamIds = bindings.map((binding) => binding?.streamId).filter(Boolean)
        if (!streamIds.length) return Promise.resolve()
        const assignedTranscoders = Stream.buildAssignedTranscodersFromBindings(bindings)

        return Stream.loadStatuses(streamIds, assignedTranscoders)
          .then((items) => {
            const statusItems = Array.isArray(items) ? items : []
            const statusesByExternalId = new Map(
              statusItems
                .filter(item => item?.externalId)
                .map(item => [item.externalId, item])
            )

            bindings.forEach((binding) => {
              const matched = statusesByExternalId.get(binding?.streamId)
              if (!matched) return

              binding.streamName = matched?.name ?? binding.streamName
              binding.status = matched?.status?.statusState ?? binding.status
              binding.statusLabel = matched?.status?.statusLabel ?? binding.statusLabel
              binding.active = matched?.status?.active ?? binding.active
              const activeFlag = matched?.status?.active
              if (activeFlag !== undefined && activeFlag !== null && !binding._streamToggleInProgress) {
                binding.enabled = !!activeFlag
              }
            })
          })
      }

      function startStreamStatusesPolling () {
        if (streamStatusesPollingId) return
        streamStatusesPollingId = setInterval(() => {
          scope.requestAssignedStreamsStatus()
        }, 10000)
      }

      function hasAssignedStreams () {
        return Array.isArray(scope.entity?.streamBindings) && scope.entity.streamBindings.some((binding) => !!binding?.streamId)
      }

      function initStreamStatusesIfReady () {
        if (streamStatusesInitialized) return
        if (!scope.entity?.id) return
        streamStatusesInitialized = true

        if (hasAssignedStreams()) {
          scope.requestAssignedStreamsStatus()
            .finally(() => {
              startStreamStatusesPolling()
            })
        }
      }

      scope.changePolicy = function () {
        scope.entity.$toPolicedStreams = { }
      }
      scope.addTranscoder = function () {
        if (!canModifyAssignmentTranscoder()) {
          alertLiteSvc.alertError('Permission denied')
          return
        }
        const modalInstance = $uibModal.open({
          animation: false,
          templateUrl: 'components/common/directives/contentItems/views/addTranscoderModal.html',
          controller: 'AddTranscoderModalCtrl',
          size: 'lg',
          resolve: {
            networkId: () => null,
            setrixServersPrefetch: () => (
              scope.setrixServersPrefetchList ||
              scope.setrixServersPrefetchPromise ||
              SetrixServer.loadAll(null, !!InappSettings?._current?.multiorgEnabled)
            ),
            assignedStreamBindings: () => {
              const list = scope.entity?.streamBindings
              if (!Array.isArray(list)) return []
              return list.map((b) => ({
                streamId: b?.streamId,
                serverId: b?.server?.id ?? null
              }))
            }
          }
        })

        modalInstance.result.then((it) => {
          const binding = toStreamBinding(it)
          if (!binding) return

          if (!Array.isArray(scope.entity.streamBindings)) {
            scope.entity.streamBindings = []
          }
          scope.entity.streamBindings.push(binding)
          scope.requestAssignedStreamsStatus()
          startStreamStatusesPolling()

          if (scope.form) {
            scope.form.$setDirty()
          }
        }).catch(() => {})
      }

      scope.deleteTranscoder = function (binding, index) {
        if (!canModifyAssignmentTranscoder()) {
          alertLiteSvc.alertError('Permission denied')
          return
        }
        if (!Array.isArray(scope.entity.streamBindings)) return
        const idx = Number.isInteger(index) ? index : scope.entity.streamBindings.indexOf(binding)
        if (idx < 0) return

        scope.entity.streamBindings.splice(idx, 1)

        if (scope.form) {
          scope.form.$setDirty()
        }
      }

      scope.onStreamEnabledChange = function (binding) {
        if (['show'].includes(scope.entity.$itemType)) return
        if (binding._streamToggleInProgress) return
        if (!authSvc?.hasAccess?.('runtimeControlTranscoder')) {
          alertLiteSvc.alertError('Permission denied')
          binding.enabled = !binding.enabled
          return
        }

        const serverId = binding?.server?.id
        const streamId = binding?.streamId
        const wantEnabled = !!binding.enabled

        if (!serverId || streamId == null) {
          alertLiteSvc.alertError('Setrix server or stream is missing')
          binding.enabled = !wantEnabled
          return
        }

        binding._streamToggleInProgress = true
        const networkId = binding?.server?.networkId ?? scope.entity?.network?.id ?? null
        const multiorgEnabled = !!InappSettings?._current?.multiorgEnabled
        const request = wantEnabled
          ? SetrixServer.startStream(serverId, streamId, networkId, multiorgEnabled)
          : SetrixServer.stopStream(serverId, streamId, networkId, multiorgEnabled)

        request
          .then(() => {
            alertLiteSvc.alert(`Transcoder ${wantEnabled ? 'started' : 'stopped'} successfully`, 'success')
            scope.requestAssignedStreamsStatus()
            if (scope.form) {
              scope.form.$setDirty()
            }
          })
          .catch(() => {
            binding.enabled = !wantEnabled
            alertLiteSvc.alertError(`Failed to ${wantEnabled ? 'started' : 'stopped'} transcoder`)
          })
          .finally(() => {
            binding._streamToggleInProgress = false
          })
      }

      scope.refreshTranscoder = function (binding) {
        if (binding?._restartInProgress) return
        if (!authSvc?.hasAccess?.('runtimeControlTranscoder')) {
          alertLiteSvc.alertError('Permission denied')
          return
        }
        const serverId = binding?.server?.id
        const streamId = binding?.streamId
        if (!serverId || streamId == null) {
          alertLiteSvc.alertError('Setrix server or stream is missing')
          return
        }
        binding._restartInProgress = true
        const networkId = binding?.server?.networkId ?? scope.entity?.network?.id ?? null
        const multiorgEnabled = !!InappSettings?._current?.multiorgEnabled
        SetrixServer.restartStream(serverId, streamId, networkId, multiorgEnabled)
          .then(() => {
            alertLiteSvc.alert('Transcoder restarted successfully', 'success')
            scope.requestAssignedStreamsStatus()
          })
          .catch((rejection) => {
            alertLiteSvc.alertError(rejection?.statusText || 'Failed to restart stream')
          })
          .finally(() => {
            binding._restartInProgress = false
          })
      }

      let defaultStreamOutputsRequestSeq = 0

      function streamBindingsOutputsSignature () {
        const bindings = Array.isArray(scope.entity?.streamBindings) ? scope.entity.streamBindings : []
        return bindings
          .map((b) => `${b?.server?.id ?? ''}:${b?.streamId ?? ''}`)
          .sort()
          .join('|')
      }

      function refreshDefaultStreamUrlOutputs () {
        const seq = ++defaultStreamOutputsRequestSeq
        const bindings = Array.isArray(scope.entity?.streamBindings) ? scope.entity.streamBindings : []
        const streamIds = bindings.map((binding) => binding?.streamId).filter(Boolean)

        if (!streamIds.length) {
          if (seq === defaultStreamOutputsRequestSeq) {
            scope.defaultStreamUrlSuggestions = []
            scope.defaultStreamUrlLoading = false
          }
          return
        }

        scope.defaultStreamUrlLoading = true
        const assignedTranscoders = Stream.buildAssignedTranscodersFromBindings(bindings)
        Stream.loadOutputs(streamIds, assignedTranscoders)
          .then((data) => {
            if (seq !== defaultStreamOutputsRequestSeq) return
            const uris = Stream.collectOutputUris(data || [])
            scope.defaultStreamUrlSuggestions = uris
            if (uris.length === 1 && scope.entity?.defaultStream?.mediaUrl && !scope.entity.defaultStream.mediaUrl.mediaUrl) {
              scope.entity.defaultStream.mediaUrl.mediaUrl = uris[0]
              changeStreamUrl({ obj: scope.entity.defaultStream })
            }
          })
          .finally(() => {
            if (seq !== defaultStreamOutputsRequestSeq) return
            scope.defaultStreamUrlLoading = false
          })
      }

      scope.getDefaultUrlMatches = function (viewValue) {
        const uriItems = Array.isArray(scope.defaultStreamUrlSuggestions)
          ? scope.defaultStreamUrlSuggestions
          : []

        const varMatches = scope.typeaheadUrl?.getMatches?.(viewValue)
        const variableItems = Array.isArray(varMatches) ? varMatches : []

        const out = []
        const seen = new Set()
        for (const item of [...uriItems, ...variableItems]) {
          if (!item) continue
          if (seen.has(item)) continue
          seen.add(item)
          out.push(item)
        }
        return out
      }

      scope.onDefaultStreamUrlSelect = function (item) {
        if (item == null) return

        const ctx = scope.typeaheadUrl?.cursorContext
        const canUseTemplateInsertion = ctx && !ctx.invalid && typeof ctx.text === 'string'

        if (canUseTemplateInsertion) {
          scope.typeaheadUrl.onSelect(item, null, { obj: scope.entity.defaultStream })
          return
        }

        if (typeof scope.setDefaultStreamUrlValue === 'function') {
          scope.setDefaultStreamUrlValue(item)
        } else {
          scope.entity.defaultStream.mediaUrl.mediaUrl = item
        }
        changeStreamUrl({ obj: scope.entity.defaultStream })
      }
      scope.changeStreamUrl = changeStreamUrl
      scope.changeDrmProfile = (platformName) => {
        if (platformName.drmProfiles.length) {
          platformName.kek = null
        }
      }
      scope.checkTrailerRequired = function (rules) {
        return ['show'].includes(scope.entity.$itemType) &&
          rules?.some(rule => rule?.then?.trailerUrl || rule?.$subRules.some(subRule => subRule?.then?.trailerUrl))
      }
      initStreamStatusesIfReady()
      const unwatchEntityId = scope.$watch('entity.id', function () {
        initStreamStatusesIfReady()
      })
      const unwatchStreamBindingsOutputs = scope.$watch(streamBindingsOutputsSignature, refreshDefaultStreamUrlOutputs)

      scope.$on('$destroy', function () {
        if (typeof unwatchEntityId === 'function') {
          unwatchEntityId()
        }
        if (typeof unwatchStreamBindingsOutputs === 'function') {
          unwatchStreamBindingsOutputs()
        }
        if (streamStatusesPollingId) {
          clearInterval(streamStatusesPollingId)
          streamStatusesPollingId = null
        }
      })
    }
  }
}

/**
 * Directive for Stream Policy Rule.
 *
 * @class ciStreamPolicyRule
 * @memberof mwApp/common
 * @see mwApp/common
 *
 * @ngdoc directive
 * @name mwApp/common:ciStreamPolicyRule
 *
 */
/* @ngInject */
export function ciStreamPolicyRule ($uibModal, UrlPolicy) {
  function getCondition (scope) {
    return (conditionRule) => UrlPolicy.getCondition(conditionRule, scope.entity)
  }
  return {
    restrict: 'E',
    templateUrl: 'components/common/directives/contentItems/views/ciStreamPolicyRule.html',
    scope: {
      form: '=form',
      rules: '=rules',
      entity: '=entity',
      drmProfiles: '=drmProfiles',
      templateVariables: '=templateVariables',
      defaultStreamUrlSuggestions: '<?',
      playbackTokenGenerators: '=playbackTokenGenerators',
      subRule: '=subRule'
    },
    link: function (scope) {
      scope.getCondition = getCondition(scope)

      scope.urlPattern = URL_PATTERN_FOR_TEMPLATES
      scope.isRuleOpen = scope.entity._isNew() || true
      scope.originalTypeaheadValue = ''

      const setUrlValue = function (val, { obj }) {
        obj.mediaUrl.mediaUrl = val
      }

      scope.typeaheadUrl = new TypeaheadClass(scope.templateVariables, setUrlValue, changeStreamUrl)

      scope.getPolicyRuleUrlMatches = function (viewValue) {
        const uriItems = Array.isArray(scope.defaultStreamUrlSuggestions)
          ? scope.defaultStreamUrlSuggestions
          : []
        const varMatches = scope.typeaheadUrl?.getMatches?.(viewValue)
        const variableItems = Array.isArray(varMatches) ? varMatches : []
        const out = []
        const seen = new Set()
        for (const item of [...uriItems, ...variableItems]) {
          if (!item) continue
          if (seen.has(item)) continue
          seen.add(item)
          out.push(item)
        }
        return out
      }

      scope.onPolicyRuleStreamUrlSelect = function (item, rule) {
        const ctx = scope.typeaheadUrl?.cursorContext
        const canUseTemplateInsertion = ctx && !ctx.invalid && typeof ctx.text === 'string'
        if (canUseTemplateInsertion) {
          scope.typeaheadUrl.onSelect(item, null, { obj: rule.then })
          return
        }
        setUrlValue(item, { obj: rule.then })
        changeStreamUrl({ obj: rule.then })
      }

      const setDefaultTrailerUrlValue = function (val, { obj, type }) {
        obj.mediaUrl[type] = val
      }
      scope.typeaheadDefaultTrailerUrl = new TypeaheadClass(scope.templateVariables, setDefaultTrailerUrlValue, changeStreamUrl)

      scope.highlightClassSimple = (model) => highlightClassSimple(scope.templateVariables, model)

      scope.changeStreamUrl = changeStreamUrl
      scope.multiSelectWithAll = multiSelectWithAll
      scope.getFilteredKeys = getFilteredKeys
      scope.filterDRM = filterDRM
      scope.initRule = function (rule) {
        const defaultRule = { mediaUrl: { drmProfiles: [] } }
        if (!rule?.then?.id) {
          rule.then = angular.copy(defaultRule)
        }
        if (scope.entity.$itemType === 'show' && !rule.then.trailerUrl) {
          rule.then.trailerUrl = ''
        }
      }

      scope.openRule = function (rule) {
        // eslint-disable-next-line no-undef
        event.stopPropagation()

        $uibModal.open({
          templateUrl: 'components/common/directives/contentItems/views/ciSPRuleDetails.html',
          controller: /* @ngInject */ function ($scope, $uibModalInstance) {
            $scope.streamPolicyOperators = streamPolicyOperators
            $scope.streamPolicyPropertiesNames = streamPolicyPropertiesNames
            $scope.getTermNames = function (terms, property) {
              return terms.map((term) => streamPolicyPropertiesNames[property].items?.[term]?.name || term).join(', ')
            }
            $scope.cancel = function () {
              $uibModalInstance.dismiss('cancel')
            }
            $scope.rule = rule
          },
          animation: false,
          size: 'lg',
          resolve: {
            rule
          }
        })
      }
    }
  }
}

/**
 * Directive for select DRM profile with connected fields
 *
 * @class ciDrmProfiles
 * @memberof mwApp/common
 * @see mwApp/common
 *
 * @ngdoc directive
 * @name mwApp/common:ciDrmProfiles
 *
 */
/* @ngInject */
export function ciDrmProfiles () {
  return {
    restrict: 'E',
    templateUrl: 'components/common/directives/contentItems/views/ciDrmProfiles.html',
    scope: {
      form: '=form',
      entity: '=entity',
      value: '=value',
      drmProfiles: '=drmProfiles',
      classItem: '=classItem',
      classContainer: '=classContainer',
      rule: '=rule',
      ruleId: '=ruleId'
    },
    link: function (scope) {
      scope.multiSelectWithAll = multiSelectWithAll
      scope.getFilteredKeys = getFilteredKeys
      scope.filterDRM = filterDRM
      scope.drm_account_types = DRM_ACCOUNT_TYPES

      scope.drms = scope.value.drmProfiles

      scope.deleteDRM = function (items) {
        if (!items.length || items[0].providerType === 'EZDRM') {
          for (const field of ['contentId']) {
            scope.value[field] = null
          }
        }

        if (!items.length || items[0].providerType !== 'EXPRESS_PLAY') {
          for (const field of ['kek']) {
            scope.value[field] = null
          }
        }

        if (!items.length || items[0].providerType !== scope.drm_account_types.IRDETO) {
          for (const field of ['entitlementPolicyId', 'bundleId']) {
            scope.value[field] = null
          }
        }
      }
    }
  }
}

/**
 * Directive for list of ACLs for item.
 *
 * @class ciAcl
 * @memberof mwApp/common
 * @see mwApp/common
 *
 * @ngdoc directive
 * @name mwApp/common:ciAcl
 *
 */
export function ciAcl () {
  return {
    restrict: 'E',
    templateUrl: 'components/common/directives/contentItems/views/ciAcl.html',
    scope: {
      entity: '=entity',
      acls: '=acls'

    },
    link: function (scope) {
    }
  }
}

export function openAssignContentSets ($state, $location, entity, isNew = false) {
  if (entity.$itemType === 'season') {
    $location.url(entity._publicUrl + '/contentSets')
    return
  }
  $state.go($state.router.globals.$current.parent.name + '.contentSets')
}

/**
 * Directive for list of assigned content sets for item.
 *
 * @class ciAssignedContentSets
 * @memberof mwApp/common
 * @see mwApp/common
 *
 * @ngdoc directive
 * @name mwApp/common:ciAssignedContentSets
 *
 */
/* @ngInject */
export function ciAssignedContentSets ($state, $location) {
  return {
    restrict: 'E',
    templateUrl: 'components/common/directives/contentItems/views/ciAssignedContentSets.html',
    scope: {
      form: '=form',
      entity: '=entity',
      allNetworks: '=?allNetworks'
    },
    link: function (scope) {
      scope.deleteContentSet = function (index) {
        scope.form.$setDirty()
        scope.entity.contentSets.splice(index, 1)
      }
    }
  }
}
